import { Container } from "unstated";
import * as voicifyApi from "../../api";
import { OrganizationEngagement, EngagementAnalyticsFilter } from "../../api/controllers/engagementAnalyticsReporting";
import ApplicationModel from "../../models/applications/api/ApplicationModel";
import {
    BillingConfig,
    RevenueConfig,
    OrganizationConfig,
    ApplicationRevenue,
    OrganizationRevenue,
    DEFAULT_BILLING_CONFIG,
    RoundingMethod,
    ApplicationBillingOverride,
    ContractType,
    ContractStatus,
    ProjectionsConfig,
    MonthlyLocationProjection,
    OrganizationProjection,
    ProjectedMonthRevenue,
    ProjectedOrganizationRevenue,
} from "../../models/revenue/BillingConfig";
import moment from "moment";

const STORAGE_KEY = "VOICIFY_REVENUE_CONFIG";
const ENGAGEMENT_PAGE_SIZE = 5000;
const REVENUE_DEBUG = false;

export interface RevenueContainerState {
    revenueConfig: RevenueConfig;
    selectedMonth: Date;
    isLoadingEngagements: boolean;
    isLoadingApplications: boolean;
    isSavingConfig: boolean;
    configSaveError: string | null;
    engagementsByOrg: Record<string, OrganizationEngagement[]>;
    applicationsByOrg: Record<string, ApplicationModel[]>;
    revenueByOrg: OrganizationRevenue[];
    projectedRevenueByOrg: ProjectedOrganizationRevenue[];
    expandedOrgs: string[];
    loadingOrgIds: string[];
    errors: string[];
}

export default class RevenueContainer extends Container<RevenueContainerState> {
    state: RevenueContainerState = {
        revenueConfig: { version: 1, organizations: [] },
        selectedMonth: moment().subtract(1, "month").startOf("month").toDate(),
        isLoadingEngagements: false,
        isLoadingApplications: false,
        isSavingConfig: false,
        configSaveError: null,
        engagementsByOrg: {},
        applicationsByOrg: {},
        revenueByOrg: [],
        projectedRevenueByOrg: [],
        expandedOrgs: [],
        loadingOrgIds: [],
        errors: [],
    };

    // ===================== ENGAGEMENT CACHE =====================
    // Cache keyed by "YYYY-MM" -> { orgId -> engagements[] }
    // Lives outside of state to avoid unnecessary re-renders on cache writes.
    private engagementCache: Record<string, Record<string, OrganizationEngagement[]>> = {};

    // ===================== WEB WORKER =====================
    // Revenue calculation worker — offloads CPU-intensive work from the UI thread.
    // Falls back to main-thread calculation if the worker cannot be created.
    private revenueWorker: Worker | null = null;
    private workerResolve: ((revenueByOrg: any[]) => void) | null = null;

    constructor() {
        super();
        try {
            this.revenueWorker = new Worker(
                new URL('../../workers/revenueCalculation.worker.ts', import.meta.url),
                { type: 'module' }
            );
            this.revenueWorker.onmessage = (event: MessageEvent) => {
                if (event.data?.type === 'calculateAll' && this.workerResolve) {
                    this.workerResolve(event.data.revenueByOrg);
                    this.workerResolve = null;
                }
            };
            this.revenueWorker.onerror = () => {
                // Worker failed — resolve with null so fallback kicks in
                if (this.workerResolve) {
                    this.workerResolve(null);
                    this.workerResolve = null;
                }
            };
        } catch {
            // Worker creation failed (e.g., unsupported environment)
            this.revenueWorker = null;
        }
    }

    /**
     * Post a calculation job to the Web Worker and return a promise
     * that resolves with the result, or null if the worker is unavailable.
     */
    private calculateViaWorker(
        orgConfigs: any[],
        engagementsByOrg: Record<string, any[]>,
        applicationsByOrg: Record<string, any[]>
    ): Promise<any[] | null> {
        if (!this.revenueWorker) return Promise.resolve(null);

        return new Promise((resolve) => {
            this.workerResolve = resolve;

            // Send only the fields the worker needs (slim payloads)
            const slimEngagements: Record<string, any[]> = {};
            for (const orgId of Object.keys(engagementsByOrg)) {
                slimEngagements[orgId] = engagementsByOrg[orgId].map((e: any) => ({
                    applicationId: e.applicationId,
                    preTransferDurationMinutes: e.preTransferDurationMinutes,
                    postTransferDurationMinutes: e.postTransferDurationMinutes,
                    sessionSummaries: e.sessionSummaries,
                    isContained: e.isContained,
                    isContainable: e.isContainable,
                    sessionTags: e.sessionTags,
                }));
            }

            const slimApps: Record<string, any[]> = {};
            for (const orgId of Object.keys(applicationsByOrg)) {
                slimApps[orgId] = applicationsByOrg[orgId].map((a: any) => ({
                    id: a.id,
                    name: a.name,
                    isLive: a.isLive,
                }));
            }

            this.revenueWorker.postMessage({
                type: 'calculateAll',
                orgConfigs,
                engagementsByOrg: slimEngagements,
                applicationsByOrg: slimApps,
            });

            // Safety timeout — if worker doesn't respond in 30s, fall back
            setTimeout(() => {
                if (this.workerResolve === resolve) {
                    this.workerResolve = null;
                    resolve(null);
                }
            }, 30000);
        });
    }

    private getMonthKey(date?: Date): string {
        return moment(date || this.state.selectedMonth).format("YYYY-MM");
    }

    private getCachedEngagements(monthKey: string, orgId: string): OrganizationEngagement[] | null {
        return this.engagementCache[monthKey]?.[orgId] ?? null;
    }

    private setCachedEngagements(monthKey: string, orgId: string, engagements: OrganizationEngagement[]) {
        if (!this.engagementCache[monthKey]) {
            this.engagementCache[monthKey] = {};
        }
        this.engagementCache[monthKey][orgId] = engagements;
    }

    clearEngagementCache() {
        this.engagementCache = {};
    }

    // ===================== CONFIG PERSISTENCE =====================

    async loadConfig(): Promise<RevenueConfig | null> {
        // Try the API first
        try {
            const result = await voicifyApi.getRevenueConfig();
            if (result.resultType === "Unauthorized") {
                window.location.href = "https://voicify.com";
                return null;
            }
            if (result.resultType === "Ok" && result.data) {
                const migratedConfig = this.migrateConfig(result.data);
                this.setState({ ...this.state, revenueConfig: migratedConfig });
                // Keep localStorage in sync as a local cache
                this.saveConfigToLocalStorage(migratedConfig);
                return migratedConfig;
            }
        } catch (e) {
            console.warn("Failed to load revenue config from API, falling back to localStorage:", e);
        }

        // Fall back to localStorage
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const config: RevenueConfig = JSON.parse(stored);
                const migratedConfig = this.migrateConfig(config);
                this.setState({ ...this.state, revenueConfig: migratedConfig });
                if (JSON.stringify(config) !== JSON.stringify(migratedConfig)) {
                    this.saveConfigToLocalStorage(migratedConfig);
                }
                return migratedConfig;
            }
        } catch (e) {
            console.error("Failed to load revenue config from localStorage:", e);
        }
        return this.state.revenueConfig;
    }

    private migrateConfig(config: RevenueConfig): RevenueConfig {
        return {
            ...config,
            organizations: config.organizations.map(org => ({
                ...org,
                forceIncludeApplicationIds: org.forceIncludeApplicationIds || [],
                contractType: org.contractType || undefined,
                contractStatus: org.contractStatus || undefined,
                totalPossibleLocations: org.totalPossibleLocations || undefined,
            }))
        };
    }

    private saveConfigToLocalStorage(config: RevenueConfig) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
        } catch (e) {
            console.error("Failed to save revenue config to localStorage:", e);
        }
    }

    // Local save (localStorage only) — used internally after each mutation
    saveConfig(config?: RevenueConfig) {
        const configToSave = config || this.state.revenueConfig;
        this.saveConfigToLocalStorage(configToSave);
    }

    // Explicit save to API — called from the Save button
    async saveConfigToApi(config?: RevenueConfig): Promise<boolean> {
        const configToSave = config || this.state.revenueConfig;
        this.setState({ ...this.state, isSavingConfig: true, configSaveError: null });
        try {
            const result = await voicifyApi.putRevenueConfig(configToSave);
            if (result.resultType === "Unauthorized") {
                window.location.href = "https://voicify.com";
                return false;
            }
            if (result.resultType === "Ok") {
                this.saveConfigToLocalStorage(configToSave);
                this.setState({ ...this.state, isSavingConfig: false, configSaveError: null });
                return true;
            }
            const errorMsg = result.errors?.join(", ") || "Failed to save configuration";
            this.setState({ ...this.state, isSavingConfig: false, configSaveError: errorMsg });
            return false;
        } catch (e) {
            const errorMsg = e?.message || "Failed to save configuration";
            this.setState({ ...this.state, isSavingConfig: false, configSaveError: errorMsg });
            return false;
        }
    }

    // ===================== ORGANIZATION MANAGEMENT =====================

    addOrganization(orgId: string, orgName?: string) {
        const existing = this.state.revenueConfig.organizations.find(
            (o) => o.organizationId === orgId
        );
        if (existing) return;

        const newOrgConfig: OrganizationConfig = {
            organizationId: orgId,
            organizationName: orgName || orgId,
            defaultConfig: { ...DEFAULT_BILLING_CONFIG },
            applicationOverrides: [],
            ignoredApplicationIds: [],
            forceIncludeApplicationIds: [],
        };

        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: [...this.state.revenueConfig.organizations, newOrgConfig],
        };

        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    removeOrganization(orgId: string) {
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: this.state.revenueConfig.organizations.filter(
                (o) => o.organizationId !== orgId
            ),
        };

        const { [orgId]: _removedEng, ...remainingEngagements } = this.state.engagementsByOrg;
        const { [orgId]: _removedApps, ...remainingApps } = this.state.applicationsByOrg;

        this.setState({
            ...this.state,
            revenueConfig: updatedConfig,
            engagementsByOrg: remainingEngagements,
            applicationsByOrg: remainingApps,
            revenueByOrg: this.state.revenueByOrg.filter(
                (r) => r.organizationId !== orgId
            ),
        });
        this.saveConfig(updatedConfig);
    }

    updateOrgContractType(orgId: string, contractType: ContractType | undefined) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) =>
            o.organizationId === orgId
                ? { ...o, contractType }
                : o
        );
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    updateOrgContractStatus(orgId: string, contractStatus: ContractStatus | undefined) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) =>
            o.organizationId === orgId
                ? { ...o, contractStatus }
                : o
        );
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    updateOrgTotalPossibleLocations(orgId: string, totalPossibleLocations: number | undefined) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) =>
            o.organizationId === orgId
                ? { ...o, totalPossibleLocations }
                : o
        );
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    updateOrgAvgRevenuePerLocation(orgId: string, avgRevenuePerLocation: number | undefined) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) =>
            o.organizationId === orgId
                ? { ...o, avgRevenuePerLocation }
                : o
        );
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
        this.calculateProjectedRevenue();
    }

    // ===================== BILLING CONFIG =====================

    updateOrgDefaultConfig(orgId: string, config: BillingConfig) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) =>
            o.organizationId === orgId
                ? { ...o, defaultConfig: { ...config } }
                : o
        );
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    addApplicationOverride(orgId: string, appId: string, config: BillingConfig) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) => {
            if (o.organizationId !== orgId) return o;
            const existingOverride = o.applicationOverrides.find(
                (ao) => ao.applicationId === appId
            );
            if (existingOverride) {
                return {
                    ...o,
                    applicationOverrides: o.applicationOverrides.map((ao) =>
                        ao.applicationId === appId ? { ...ao, config: { ...config } } : ao
                    ),
                };
            }
            return {
                ...o,
                applicationOverrides: [
                    ...o.applicationOverrides,
                    { applicationId: appId, config: { ...config } },
                ],
            };
        });
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    removeApplicationOverride(orgId: string, appId: string) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) => {
            if (o.organizationId !== orgId) return o;
            return {
                ...o,
                applicationOverrides: o.applicationOverrides.filter(
                    (ao) => ao.applicationId !== appId
                ),
            };
        });
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    copyBillingConfig(config: BillingConfig, targetOrgId: string, targetAppId?: string) {
        if (targetAppId) {
            this.addApplicationOverride(targetOrgId, targetAppId, config);
        } else {
            this.updateOrgDefaultConfig(targetOrgId, config);
        }
    }

    // ===================== EXPORT/IMPORT =====================

    exportOrgConfig(orgId: string): string {
        const org = this.state.revenueConfig.organizations.find(o => o.organizationId === orgId);
        if (!org) {
            throw new Error(`Organization ${orgId} not found`);
        }
        return JSON.stringify(org, null, 2);
    }

    exportAppConfig(orgId: string, appId: string): string {
        const org = this.state.revenueConfig.organizations.find(o => o.organizationId === orgId);
        if (!org) {
            throw new Error(`Organization ${orgId} not found`);
        }
        const override = org.applicationOverrides.find(ao => ao.applicationId === appId);
        if (!override) {
            throw new Error(`Application override for ${appId} not found`);
        }
        return JSON.stringify(override, null, 2);
    }

    importOrgConfig(jsonString: string): { success: boolean; error?: string } {
        try {
            const orgConfig: OrganizationConfig = JSON.parse(jsonString);
            
            // Validate structure
            if (!orgConfig.organizationId || !orgConfig.defaultConfig) {
                return { success: false, error: "Invalid organization config format" };
            }

            // Check if org already exists
            const existingIndex = this.state.revenueConfig.organizations.findIndex(
                o => o.organizationId === orgConfig.organizationId
            );

            let updatedOrgs: OrganizationConfig[];
            if (existingIndex >= 0) {
                // Replace existing org config
                updatedOrgs = this.state.revenueConfig.organizations.map((o, idx) =>
                    idx === existingIndex ? { ...orgConfig } : o
                );
            } else {
                // Add new org config
                updatedOrgs = [...this.state.revenueConfig.organizations, { ...orgConfig }];
            }

            const updatedConfig: RevenueConfig = {
                ...this.state.revenueConfig,
                organizations: updatedOrgs,
            };

            this.setState({ ...this.state, revenueConfig: updatedConfig });
            this.saveConfig(updatedConfig);

            return { success: true };
        } catch (e) {
            return { success: false, error: `Failed to parse JSON: ${e.message}` };
        }
    }

    importAppConfig(orgId: string, jsonString: string): { success: boolean; error?: string } {
        try {
            const appOverride: ApplicationBillingOverride = JSON.parse(jsonString);
            
            // Validate structure
            if (!appOverride.applicationId || !appOverride.config) {
                return { success: false, error: "Invalid application config format" };
            }

            // Check if org exists
            const orgExists = this.state.revenueConfig.organizations.some(
                o => o.organizationId === orgId
            );
            if (!orgExists) {
                return { success: false, error: `Organization ${orgId} not found` };
            }

            this.addApplicationOverride(orgId, appOverride.applicationId, appOverride.config);

            return { success: true };
        } catch (e) {
            return { success: false, error: `Failed to parse JSON: ${e.message}` };
        }
    }

    // ===================== IGNORED APPLICATIONS =====================

    toggleIgnoreApplication(orgId: string, appId: string) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) => {
            if (o.organizationId !== orgId) return o;
            const isIgnored = o.ignoredApplicationIds.includes(appId);
            return {
                ...o,
                ignoredApplicationIds: isIgnored
                    ? o.ignoredApplicationIds.filter((id) => id !== appId)
                    : [...o.ignoredApplicationIds, appId],
            };
        });
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
        
        // Recalculate revenue immediately to update totals
        this.calculateAllRevenue();
    }

    // ===================== FORCE INCLUDE APPLICATIONS =====================

    addForceIncludeApplication(orgId: string, appId: string) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) => {
            if (o.organizationId !== orgId) return o;
            if (o.forceIncludeApplicationIds.includes(appId)) return o;
            return {
                ...o,
                forceIncludeApplicationIds: [...o.forceIncludeApplicationIds, appId],
            };
        });
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    removeForceIncludeApplication(orgId: string, appId: string) {
        const updatedOrgs = this.state.revenueConfig.organizations.map((o) => {
            if (o.organizationId !== orgId) return o;
            return {
                ...o,
                forceIncludeApplicationIds: o.forceIncludeApplicationIds.filter(
                    (id) => id !== appId
                ),
            };
        });
        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            organizations: updatedOrgs,
        };
        this.setState({ ...this.state, revenueConfig: updatedConfig });
        this.saveConfig(updatedConfig);
    }

    // ===================== UI STATE =====================

    toggleOrgExpanded(orgId: string) {
        const isExpanded = this.state.expandedOrgs.includes(orgId);
        this.setState({
            ...this.state,
            expandedOrgs: isExpanded
                ? this.state.expandedOrgs.filter((id) => id !== orgId)
                : [...this.state.expandedOrgs, orgId],
        });
    }

    setSelectedMonth(month: Date) {
        this.setState({ ...this.state, selectedMonth: month });
    }

    // ===================== DATA LOADING =====================

    async loadApplicationsForOrg(orgId: string): Promise<ApplicationModel[]> {
        // First try to get all apps using the standard endpoint
        const result = await voicifyApi.getApplicationsForOrg(orgId);
        if (result.resultType === "Ok" && result.data) {
            return result.data;
        }

        // Fallback: try paginated search if the standard endpoint fails
        const allApps: ApplicationModel[] = [];
        let skip = 0;
        const take = 100;
        let hasMore = true;

        while (hasMore) {
            const searchResult = await voicifyApi.searchForApplications(orgId, skip, take, "name", "");
            if (searchResult.resultType === "Ok" && searchResult.data?.applications) {
                const page = searchResult.data.applications;
                for (let i = 0; i < page.length; i++) {
                    allApps.push(page[i]);
                }
                
                hasMore = page.length === take;
                skip += take;
            } else {
                hasMore = false;
            }
        }

        return allApps;
    }

    async loadEngagementsForOrg(
        orgId: string,
        startDate: string,
        endDate: string,
        orgName?: string
    ): Promise<OrganizationEngagement[]> {
        const allEngagements: OrganizationEngagement[] = [];
        let skip = 0;
        let hasMore = true;

        while (hasMore) {
            const filter: EngagementAnalyticsFilter = {
                startDate,
                endDate,
                skip,
                take: ENGAGEMENT_PAGE_SIZE,
            };

            const result = await voicifyApi.getOrganizationEngagements(orgId, filter);
            if (result.resultType === "Ok") {
                const page = result.data;
                if (REVENUE_DEBUG) {
                    console.log(`[Load Engagements] Org ${orgId}, skip ${skip}: received ${page?.length || 0} engagements`);
                }
                // Use push loop instead of spread to avoid O(n²) copying
                for (let i = 0; i < page.length; i++) {
                    allEngagements.push(page[i]);
                }
                if (page.length < ENGAGEMENT_PAGE_SIZE) {
                    hasMore = false;
                } else {
                    skip += ENGAGEMENT_PAGE_SIZE;
                }
            } else {
                const orgDisplay = orgName ? `${orgName} (${orgId})` : orgId;
                this.setState((prevState) => ({
                    ...prevState,
                    errors: [
                        ...prevState.errors,
                        `Failed to load engagements for org ${orgDisplay}: ${result.errors?.[0] || "Unknown error"}`,
                    ],
                }));
                hasMore = false;
            }
        }

        return allEngagements;
    }

    async loadAllData(forceReload: boolean = false) {
        const orgs = this.state.revenueConfig.organizations;
        if (orgs.length === 0) return;

        const monthKey = this.getMonthKey();
        const startDate = moment(this.state.selectedMonth)
            .startOf("month")
            .format("YYYY-MM-DDTHH:mm:ss.SSSZ");
        const endDate = moment(this.state.selectedMonth)
            .endOf("month")
            .format("YYYY-MM-DDTHH:mm:ss.SSSZ");

        if (REVENUE_DEBUG) console.log(`[Load Data] Date range: ${startDate} to ${endDate}`);

        // Determine which orgs need fetching vs which have cached data
        const orgsToFetch: typeof orgs = [];
        const cachedEngagementsByOrg: Record<string, OrganizationEngagement[]> = {};

        if (forceReload) {
            // Clear cache for this month if force reloading
            if (this.engagementCache[monthKey]) {
                delete this.engagementCache[monthKey];
            }
            orgsToFetch.push(...orgs);
        } else {
            for (const org of orgs) {
                const cached = this.getCachedEngagements(monthKey, org.organizationId);
                if (cached) {
                    cachedEngagementsByOrg[org.organizationId] = cached;
                } else {
                    orgsToFetch.push(org);
                }
            }
        }

        this.setState((prevState) => ({
            ...prevState,
            isLoadingEngagements: true,
            isLoadingApplications: true,
            errors: [],
            loadingOrgIds: orgsToFetch.map((o) => o.organizationId),
            // Immediately populate cached data so UI shows them
            engagementsByOrg: { ...prevState.engagementsByOrg, ...cachedEngagementsByOrg },
        }));

        const newEngagementsByOrg: Record<string, OrganizationEngagement[]> = { ...cachedEngagementsByOrg };
        const newApplicationsByOrg: Record<string, ApplicationModel[]> = {};

        // Load applications and engagements concurrently using a shared
        // concurrency-limited queue. Each slot picks up the next org as soon
        // as it finishes, so no slot sits idle.
        const CONCURRENCY = 5;

        // --- Applications queue (all orgs, lightweight) ---
        let appNextIndex = 0;
        const processApps = async (): Promise<void> => {
            while (appNextIndex < orgs.length) {
                const idx = appNextIndex++;
                const org = orgs[idx];
                const apps = await this.loadApplicationsForOrg(org.organizationId);
                newApplicationsByOrg[org.organizationId] = apps;

                this.setState((prevState) => ({
                    ...prevState,
                    applicationsByOrg: {
                        ...prevState.applicationsByOrg,
                        [org.organizationId]: apps,
                    },
                }));
            }
        };

        const appWorkers = [];
        for (let w = 0; w < Math.min(CONCURRENCY, orgs.length); w++) {
            appWorkers.push(processApps());
        }

        // --- Engagements queue (only uncached orgs) ---
        let engNextIndex = 0;
        const processEngagements = async (): Promise<void> => {
            while (engNextIndex < orgsToFetch.length) {
                const idx = engNextIndex++;
                const org = orgsToFetch[idx];

                const engagements = await this.loadEngagementsForOrg(
                    org.organizationId,
                    startDate,
                    endDate,
                    org.organizationName
                );

                // Store result and cache it
                newEngagementsByOrg[org.organizationId] = engagements;
                this.setCachedEngagements(monthKey, org.organizationId, engagements);
                if (REVENUE_DEBUG) console.log(`[Load Data] Loaded ${org.organizationId}: ${engagements.length} engagements`);

                // Update state immediately so user sees progress per-org
                this.setState((prevState) => ({
                    ...prevState,
                    engagementsByOrg: {
                        ...prevState.engagementsByOrg,
                        [org.organizationId]: engagements,
                    },
                    loadingOrgIds: prevState.loadingOrgIds.filter(
                        (id) => id !== org.organizationId
                    ),
                }));

                // Progressive revenue calculation for this org immediately
                this.calculateRevenueForOrgs(
                    [org.organizationId],
                    newEngagementsByOrg,
                    newApplicationsByOrg
                );
            }
        };

        const engWorkers = [];
        if (orgsToFetch.length > 0) {
            for (let w = 0; w < Math.min(CONCURRENCY, orgsToFetch.length); w++) {
                engWorkers.push(processEngagements());
            }
        }

        // Wait for both queues to finish
        await Promise.all([...appWorkers, ...engWorkers]);

        // Calculate revenue for all orgs now that both apps and engagements are fully loaded.
        // Cached orgs were never calculated mid-flight; fetched orgs were calculated progressively
        // but may have had incomplete app metadata at that point — recalculate both.
        const allLoadedOrgIds = Array.from(new Set([
            ...Object.keys(cachedEngagementsByOrg),
            ...Object.keys(newEngagementsByOrg),
        ]));
        if (allLoadedOrgIds.length > 0) {
            this.calculateRevenueForOrgs(
                allLoadedOrgIds,
                newEngagementsByOrg,
                newApplicationsByOrg
            );
        }

        if (REVENUE_DEBUG) console.log(`[Load Data] Final newEngagementsByOrg:`, Object.keys(newEngagementsByOrg).map(k => `${k}: ${newEngagementsByOrg[k].length} engagements`));

        this.setState((prevState) => ({
            ...prevState,
            isLoadingEngagements: false,
            isLoadingApplications: false,
            engagementsByOrg: { ...prevState.engagementsByOrg, ...newEngagementsByOrg },
            applicationsByOrg: { ...prevState.applicationsByOrg, ...newApplicationsByOrg },
            loadingOrgIds: [],
        }));
    }

    async loadDataForSingleOrg(orgId: string, forceReload: boolean = false) {
        const monthKey = this.getMonthKey();
        const startDate = moment(this.state.selectedMonth)
            .startOf("month")
            .format("YYYY-MM-DDTHH:mm:ss.SSSZ");
        const endDate = moment(this.state.selectedMonth)
            .endOf("month")
            .format("YYYY-MM-DDTHH:mm:ss.SSSZ");

        // Check cache first (unless force reloading)
        const cached = !forceReload ? this.getCachedEngagements(monthKey, orgId) : null;

        this.setState((prevState) => ({
            ...prevState,
            loadingOrgIds: [...prevState.loadingOrgIds, orgId],
            isLoadingEngagements: true,
        }));

        // Always reload apps (lightweight), only reload engagements if not cached
        const orgConfig = this.state.revenueConfig.organizations.find(
            (o) => o.organizationId === orgId
        );
        const [apps, engagements] = await Promise.all([
            this.loadApplicationsForOrg(orgId),
            cached
                ? Promise.resolve(cached)
                : this.loadEngagementsForOrg(orgId, startDate, endDate, orgConfig?.organizationName),
        ]);

        // Cache the engagements if freshly fetched
        if (!cached) {
            this.setCachedEngagements(monthKey, orgId, engagements);
        }

        const newEngagementsByOrg = {
            ...this.state.engagementsByOrg,
            [orgId]: engagements,
        };
        const newApplicationsByOrg = {
            ...this.state.applicationsByOrg,
            [orgId]: apps,
        };

        this.setState((prevState) => ({
            ...prevState,
            isLoadingEngagements: false,
            engagementsByOrg: {
                ...prevState.engagementsByOrg,
                [orgId]: engagements,
            },
            applicationsByOrg: {
                ...prevState.applicationsByOrg,
                [orgId]: apps,
            },
            loadingOrgIds: prevState.loadingOrgIds.filter((id) => id !== orgId),
        }));

        this.calculateAllRevenue(newEngagementsByOrg, newApplicationsByOrg);
    }

    // ===================== REVENUE CALCULATION =====================

    applyRounding(minutes: number, method: RoundingMethod): number {
        if (!minutes || minutes <= 0) return 0;
        switch (method) {
            case "up":
                return Math.ceil(minutes);
            case "down":
                return Math.floor(minutes);
            case "nearest":
                return Math.round(minutes);
            case "15sec":
                return Math.ceil(minutes * 4) / 4;
            case "30sec":
                return Math.ceil(minutes * 2) / 2;
            case "none":
                return minutes;
            default:
                return Math.ceil(minutes);
        }
    }

    getCallCount(sessionSummaries?: string): number {
        if (!sessionSummaries) return 1;
        // Fast path: count top-level keys by scanning for `":{` or `":` patterns
        // at brace depth 1, avoiding a full JSON.parse for each engagement.
        // The format is a JSON object like {"key1":{...},"key2":{...}}
        try {
            const str = sessionSummaries.trim();
            if (str.length < 2 || str[0] !== '{') return 1;
            let keyCount = 0;
            let depth = 0;
            let inString = false;
            let escape = false;
            for (let i = 0; i < str.length; i++) {
                const ch = str[i];
                if (escape) {
                    escape = false;
                    continue;
                }
                if (ch === '\\' && inString) {
                    escape = true;
                    continue;
                }
                if (ch === '"') {
                    inString = !inString;
                    continue;
                }
                if (inString) continue;
                if (ch === '{') {
                    depth++;
                } else if (ch === '}') {
                    depth--;
                } else if (ch === ':' && depth === 1) {
                    keyCount++;
                }
            }
            return keyCount > 0 ? keyCount : 1;
        } catch {
            return 1;
        }
    }

    calculateApplicationRevenue(
        appId: string,
        appName: string,
        engagements: OrganizationEngagement[],
        config: BillingConfig
    ): ApplicationRevenue {
        const breakdown = {
            aiMinutes: { count: 0, revenue: 0 },
            postTransferMinutes: { count: 0, revenue: 0 },
            calls: { count: 0, revenue: 0 },
            containedCalls: { count: 0, revenue: 0 },
            nonContainableCalls: { count: 0, revenue: 0 },
            ordersPlaced: { count: 0, revenue: 0 },
            flatFee: config.flatMonthlyFee,
        };

        for (const engagement of engagements) {
            // AI minutes (preTransferDurationMinutes)
            const aiMin = this.applyRounding(
                engagement.preTransferDurationMinutes || 0,
                config.roundingMethod
            );
            breakdown.aiMinutes.count += aiMin;

            // Post transfer minutes (postTransferDurationMinutes)
            const ptMin = this.applyRounding(
                engagement.postTransferDurationMinutes || 0,
                config.roundingMethod
            );
            breakdown.postTransferMinutes.count += ptMin;

            // Calls
            const callCount = this.getCallCount(engagement.sessionSummaries);
            breakdown.calls.count += callCount;

            // Contained calls
            if (engagement.isContained) {
                breakdown.containedCalls.count += 1;
            }

            // Non-containable calls
            if (engagement.isContainable === false) {
                breakdown.nonContainableCalls.count += 1;
            }

            // Orders placed - check if ACTION_COMPLETED is in sessionTags array
            if (engagement.sessionTags?.includes("ACTION_COMPLETED")) {
                breakdown.ordersPlaced.count += 1;
            }
        }

        // Apply minutes included (across AI + post-transfer combined)
        const totalMinutes = breakdown.aiMinutes.count + breakdown.postTransferMinutes.count;
        const billableMinutes = Math.max(0, totalMinutes - config.minutesIncluded);

        if (totalMinutes > 0 && billableMinutes > 0) {
            const aiRatio = breakdown.aiMinutes.count / totalMinutes;
            const ptRatio = breakdown.postTransferMinutes.count / totalMinutes;
            breakdown.aiMinutes.revenue =
                billableMinutes * aiRatio * config.aiMinuteRate;
            breakdown.postTransferMinutes.revenue =
                billableMinutes * ptRatio * config.postTransferMinuteRate;
        }

        // Apply calls included
        const billableCalls = Math.max(0, breakdown.calls.count - config.callsIncluded);
        breakdown.calls.revenue = billableCalls * config.callRate;

        // Contained and non-containable are not subject to "included" amounts
        breakdown.containedCalls.revenue =
            breakdown.containedCalls.count * config.containedCallRate;
        breakdown.nonContainableCalls.revenue =
            breakdown.nonContainableCalls.count * config.nonContainableCallRate;
        breakdown.ordersPlaced.revenue =
            breakdown.ordersPlaced.count * config.orderPlacedRate;

        const totalRevenue =
            breakdown.aiMinutes.revenue +
            breakdown.postTransferMinutes.revenue +
            breakdown.calls.revenue +
            breakdown.containedCalls.revenue +
            breakdown.nonContainableCalls.revenue +
            breakdown.ordersPlaced.revenue +
            breakdown.flatFee;

        return {
            applicationId: appId,
            applicationName: appName,
            totalRevenue,
            breakdown,
            engagementCount: engagements.length,
        };
    }

    /**
     * Calculate revenue for a specific subset of org IDs and merge results
     * into the existing revenueByOrg state. Used for progressive calculation
     * as data batches arrive, so the UI updates incrementally.
     */
    calculateRevenueForOrgs(
        orgIds: string[],
        engagementsByOrgData: Record<string, OrganizationEngagement[]>,
        applicationsByOrgData: Record<string, ApplicationModel[]>
    ) {
        const newOrgRevenues: OrganizationRevenue[] = [];

        for (const orgId of orgIds) {
            const orgConfig = this.state.revenueConfig.organizations.find(
                (o) => o.organizationId === orgId
            );
            if (!orgConfig) continue;

            const engagements = engagementsByOrgData[orgId] || [];
            const apps = applicationsByOrgData[orgId] || [];

            if (REVENUE_DEBUG) console.log(`[Progressive Calc] Org ${orgId}: ${engagements.length} engagements, ${apps.length} apps`);

            // Group engagements by applicationId
            const engagementsByApp: Record<string, OrganizationEngagement[]> = {};
            for (const eng of engagements) {
                const appId = eng.applicationId;
                if (!appId) continue;
                if (!engagementsByApp[appId]) {
                    engagementsByApp[appId] = [];
                }
                engagementsByApp[appId].push(eng);
            }

            const applicationRevenues: ApplicationRevenue[] = [];

            // Get all unique application IDs
            const allAppIds = new Set([
                ...Object.keys(engagementsByApp),
                ...apps.map((a) => a.id).filter(Boolean),
                ...(orgConfig.forceIncludeApplicationIds ?? []),
            ]);

            for (const appId of allAppIds) {
                if (orgConfig.ignoredApplicationIds.includes(appId)) continue;

                const appEngagements = engagementsByApp[appId] || [];
                const isForceIncluded = (orgConfig.forceIncludeApplicationIds ?? []).includes(appId);
                if (appEngagements.length === 0 && !isForceIncluded) continue;

                const appModel = apps.find((a) => a.id === appId);

                // Skip non-live apps unless explicitly force-included
                if (appModel && appModel.isLive === false && !isForceIncluded) continue;

                const appName = appModel?.name || appId;

                const override = orgConfig.applicationOverrides.find(
                    (ao) => ao.applicationId === appId
                );
                const config = override ? override.config : orgConfig.defaultConfig;

                const appRevenue = this.calculateApplicationRevenue(
                    appId,
                    appName,
                    appEngagements,
                    config
                );
                applicationRevenues.push(appRevenue);
            }

            applicationRevenues.sort((a, b) =>
                a.applicationName.toLowerCase().localeCompare(b.applicationName.toLowerCase())
            );

            const orgTotal = applicationRevenues.reduce(
                (sum, ar) => sum + ar.totalRevenue,
                0
            );

            newOrgRevenues.push({
                organizationId: orgId,
                organizationName: orgConfig.organizationName || orgId,
                totalRevenue: orgTotal,
                applications: applicationRevenues,
            });
        }

        // Merge: replace existing entries for these orgIds, keep others
        const orgIdSet = new Set(orgIds);
        this.setState((prevState) => ({
            ...prevState,
            revenueByOrg: [
                ...prevState.revenueByOrg.filter((r) => !orgIdSet.has(r.organizationId)),
                ...newOrgRevenues,
            ],
        }));
    }

    calculateAllRevenue(
        engagementsByOrgOverride?: Record<string, OrganizationEngagement[]>,
        applicationsByOrgOverride?: Record<string, ApplicationModel[]>
    ) {
        const engagementsByOrgData = engagementsByOrgOverride || this.state.engagementsByOrg;
        const applicationsByOrgData = applicationsByOrgOverride || this.state.applicationsByOrg;

        // Try the Web Worker first for large datasets (off main thread)
        const totalEngagements = Object.values(engagementsByOrgData).reduce(
            (sum, arr) => sum + arr.length, 0
        );

        if (this.revenueWorker && totalEngagements > 5000) {
            if (REVENUE_DEBUG) console.log(`[Revenue Calc] Offloading ${totalEngagements} engagements to Web Worker`);
            this.calculateViaWorker(
                this.state.revenueConfig.organizations,
                engagementsByOrgData,
                applicationsByOrgData
            ).then((workerResult) => {
                if (workerResult) {
                    this.setState({
                        ...this.state,
                        revenueByOrg: workerResult,
                        isLoadingEngagements: false,
                        isLoadingApplications: false,
                        loadingOrgIds: [],
                    });
                } else {
                    // Worker failed — fall back to main thread
                    if (REVENUE_DEBUG) console.log(`[Revenue Calc] Worker failed, falling back to main thread`);
                    this.calculateAllRevenueMainThread(engagementsByOrgData, applicationsByOrgData);
                }
            });
            return;
        }

        // Small dataset or no worker — calculate on main thread directly
        this.calculateAllRevenueMainThread(engagementsByOrgData, applicationsByOrgData);
    }

    /**
     * Main-thread revenue calculation — used as fallback when the Web Worker
     * is unavailable, or for small datasets where the overhead isn't worth it.
     */
    private calculateAllRevenueMainThread(
        engagementsByOrgData: Record<string, OrganizationEngagement[]>,
        applicationsByOrgData: Record<string, ApplicationModel[]>
    ) {
        const revenueByOrg: OrganizationRevenue[] = [];

        for (const orgConfig of this.state.revenueConfig.organizations) {
            const orgId = orgConfig.organizationId;
            const engagements = engagementsByOrgData[orgId] || [];
            const apps = applicationsByOrgData[orgId] || [];

            if (REVENUE_DEBUG) console.log(`[Revenue Calc] Org ${orgId}: ${engagements.length} engagements, ${apps.length} apps`);

            // Group engagements by applicationId
            const engagementsByApp: Record<string, OrganizationEngagement[]> = {};
            let skippedCount = 0;
            for (const eng of engagements) {
                const appId = eng.applicationId;
                if (!appId) {
                    skippedCount++;
                    continue; // Skip engagements without applicationId
                }
                if (!engagementsByApp[appId]) {
                    engagementsByApp[appId] = [];
                }
                engagementsByApp[appId].push(eng);
            }

            if (REVENUE_DEBUG && skippedCount > 0) {
                console.log(`[Revenue Calc] Skipped ${skippedCount} engagements without applicationId`);
            }
            if (REVENUE_DEBUG) console.log(`[Revenue Calc] Grouped into ${Object.keys(engagementsByApp).length} apps with engagements`);

            const applicationRevenues: ApplicationRevenue[] = [];

            // Get all unique application IDs from engagements and force-include list
            const allAppIds = new Set([
                ...Object.keys(engagementsByApp),
                ...apps.map((a) => a.id).filter(Boolean),
                ...(orgConfig.forceIncludeApplicationIds ?? []),
            ]);

            if (REVENUE_DEBUG) console.log(`[Revenue Calc] Processing ${allAppIds.size} unique app IDs`);

            for (const appId of allAppIds) {
                // Skip ignored applications
                if (orgConfig.ignoredApplicationIds.includes(appId)) {
                    continue;
                }

                const appEngagements = engagementsByApp[appId] || [];
                const isForceIncluded = (orgConfig.forceIncludeApplicationIds ?? []).includes(appId);
                
                // Skip apps with 0 engagements unless they're force-included
                if (appEngagements.length === 0 && !isForceIncluded) {
                    continue;
                }

                // Find application name
                const appModel = apps.find((a) => a.id === appId);

                // Skip non-live apps unless explicitly force-included
                if (appModel && appModel.isLive === false && !isForceIncluded) {
                    continue;
                }

                const appName = appModel?.name || appId;

                // Determine config: app override or org default
                const override = orgConfig.applicationOverrides.find(
                    (ao) => ao.applicationId === appId
                );
                const config = override ? override.config : orgConfig.defaultConfig;

                const appRevenue = this.calculateApplicationRevenue(
                    appId,
                    appName,
                    appEngagements,
                    config
                );
                if (REVENUE_DEBUG) console.log(`[Revenue Calc] App ${appName}: ${appEngagements.length} engagements, $${appRevenue.totalRevenue.toFixed(2)} revenue${isForceIncluded ? ' (force-included)' : ''}`);
                applicationRevenues.push(appRevenue);
            }

            // Sort by application name alphabetically
            applicationRevenues.sort((a, b) => 
                a.applicationName.toLowerCase().localeCompare(b.applicationName.toLowerCase())
            );

            const orgTotal = applicationRevenues.reduce(
                (sum, ar) => sum + ar.totalRevenue,
                0
            );

            if (REVENUE_DEBUG) console.log(`[Revenue Calc] Org ${orgId} total: ${applicationRevenues.length} apps, $${orgTotal.toFixed(2)} revenue`);

            revenueByOrg.push({
                organizationId: orgId,
                organizationName: orgConfig.organizationName || orgId,
                totalRevenue: orgTotal,
                applications: applicationRevenues,
            });
        }

        this.setState({
            ...this.state,
            revenueByOrg,
            isLoadingEngagements: false,
            isLoadingApplications: false,
            loadingOrgIds: [],
        });
    }

    // ===================== PROJECTIONS =====================

    getCurrentLocationCount(orgId: string): number {
        // Use tracked apps from revenue data (apps that appear in revenueByOrg)
        const orgRevenue = this.state.revenueByOrg.find(
            (r) => r.organizationId === orgId
        );
        if (orgRevenue && orgRevenue.applications.length > 0) {
            return orgRevenue.applications.length;
        }

        // Fallback: filter apps by non-ignored
        const orgConfig = this.state.revenueConfig.organizations.find(
            (o) => o.organizationId === orgId
        );
        const apps = this.state.applicationsByOrg[orgId] || [];
        if (orgConfig && apps.length > 0) {
            return apps.filter(
                (a) => !orgConfig.ignoredApplicationIds.includes(a.id)
            ).length;
        }

        return 0;
    }

    getAverageRevenuePerLocation(orgId: string): number {
        // Prefer the manually configured override if set
        const orgConfig = this.state.revenueConfig.organizations.find(
            (o) => o.organizationId === orgId
        );
        if (orgConfig?.avgRevenuePerLocation != null && orgConfig.avgRevenuePerLocation > 0) {
            return orgConfig.avgRevenuePerLocation;
        }

        // Fall back to computing from actual loaded revenue data
        const orgRevenue = this.state.revenueByOrg.find(
            (r) => r.organizationId === orgId
        );
        if (!orgRevenue || orgRevenue.applications.length === 0) return 0;
        return orgRevenue.totalRevenue / orgRevenue.applications.length;
    }

    getOrganizationProjection(orgId: string): OrganizationProjection | undefined {
        return this.state.revenueConfig.projections?.organizationProjections.find(
            (op) => op.organizationId === orgId
        );
    }

    /**
     * Initialize projections for multiple orgs in a single setState call.
     * Only initializes orgs that don't already have projections.
     * Calls calculateProjectedRevenue once after state settles.
     */
    initializeAllMissingProjections(orgIds: string[], monthCount: number = 12) {
        const startMonth = moment().add(1, "month").startOf("month");
        let projections = this.state.revenueConfig.projections || {
            version: 1,
            organizationProjections: [],
        };

        let updatedOrgProjections = [...projections.organizationProjections];
        let changed = false;

        for (const orgId of orgIds) {
            const existing = updatedOrgProjections.find(
                (op) => op.organizationId === orgId
            );
            if (existing && existing.monthlyProjections.length > 0) continue;

            const currentLocationCount = this.getCurrentLocationCount(orgId);
            const monthlyProjections: MonthlyLocationProjection[] = [];
            for (let i = 0; i < monthCount; i++) {
                monthlyProjections.push({
                    month: moment(startMonth).add(i, "months").format("YYYY-MM"),
                    locationCount: currentLocationCount,
                });
            }

            // Remove any existing (empty) entry, then add new
            updatedOrgProjections = updatedOrgProjections.filter(
                (op) => op.organizationId !== orgId
            );
            updatedOrgProjections.push({
                organizationId: orgId,
                monthlyProjections,
            });
            changed = true;
        }

        if (!changed) {
            // Nothing to initialize, just calculate
            this.calculateProjectedRevenue();
            return;
        }

        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            projections: {
                ...projections,
                organizationProjections: updatedOrgProjections,
            },
        };

        this.setState({ ...this.state, revenueConfig: updatedConfig }).then(() => {
            this.calculateProjectedRevenue();
        });
        this.saveConfig(updatedConfig);
    }

    initializeDefaultProjections(orgId: string, monthCount: number = 12) {
        const currentLocationCount = this.getCurrentLocationCount(orgId);
        const startMonth = moment().add(1, "month").startOf("month");

        const monthlyProjections: MonthlyLocationProjection[] = [];
        for (let i = 0; i < monthCount; i++) {
            monthlyProjections.push({
                month: moment(startMonth).add(i, "months").format("YYYY-MM"),
                locationCount: currentLocationCount,
            });
        }

        const projections = this.state.revenueConfig.projections || {
            version: 1,
            organizationProjections: [],
        };

        // Remove any existing projection for this org, then add new
        const updatedOrgProjections = projections.organizationProjections.filter(
            (op) => op.organizationId !== orgId
        );
        updatedOrgProjections.push({
            organizationId: orgId,
            monthlyProjections,
        });

        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            projections: {
                ...projections,
                organizationProjections: updatedOrgProjections,
            },
        };

        this.setState({ ...this.state, revenueConfig: updatedConfig }).then(() => {
            this.calculateProjectedRevenue();
        });
        this.saveConfig(updatedConfig);
    }

    updateProjection(orgId: string, month: string, locationCount: number) {
        const projections = this.state.revenueConfig.projections || {
            version: 1,
            organizationProjections: [],
        };

        const orgProjectionIndex = projections.organizationProjections.findIndex(
            (op) => op.organizationId === orgId
        );

        let updatedOrgProjections = [...projections.organizationProjections];

        if (orgProjectionIndex >= 0) {
            const orgProjection = updatedOrgProjections[orgProjectionIndex];
            const monthIndex = orgProjection.monthlyProjections.findIndex(
                (mp) => mp.month === month
            );

            let updatedMonthly = [...orgProjection.monthlyProjections];
            if (monthIndex >= 0) {
                updatedMonthly[monthIndex] = {
                    month,
                    locationCount: Math.floor(locationCount),
                };
            } else {
                updatedMonthly.push({
                    month,
                    locationCount: Math.floor(locationCount),
                });
                // Sort by month
                updatedMonthly.sort((a, b) => a.month.localeCompare(b.month));
            }

            updatedOrgProjections[orgProjectionIndex] = {
                ...orgProjection,
                monthlyProjections: updatedMonthly,
            };
        } else {
            updatedOrgProjections.push({
                organizationId: orgId,
                monthlyProjections: [
                    { month, locationCount: Math.floor(locationCount) },
                ],
            });
        }

        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            projections: {
                ...projections,
                organizationProjections: updatedOrgProjections,
            },
        };

        this.setState({ ...this.state, revenueConfig: updatedConfig }).then(() => {
            this.calculateProjectedRevenue();
        });
        this.saveConfig(updatedConfig);
    }

    addProjectionMonth(orgId: string) {
        const orgProjection = this.getOrganizationProjection(orgId);
        const currentLocationCount = this.getCurrentLocationCount(orgId);

        let nextMonth: string;
        if (orgProjection && orgProjection.monthlyProjections.length > 0) {
            const lastMonth = orgProjection.monthlyProjections[orgProjection.monthlyProjections.length - 1].month;
            nextMonth = moment(lastMonth, "YYYY-MM").add(1, "month").format("YYYY-MM");
        } else {
            nextMonth = moment().add(1, "month").startOf("month").format("YYYY-MM");
        }

        // updateProjection already chains calculateProjectedRevenue
        this.updateProjection(orgId, nextMonth, currentLocationCount);
    }

    removeProjectionMonth(orgId: string, month: string) {
        const projections = this.state.revenueConfig.projections;
        if (!projections) return;

        const updatedOrgProjections = projections.organizationProjections.map((op) => {
            if (op.organizationId !== orgId) return op;
            return {
                ...op,
                monthlyProjections: op.monthlyProjections.filter(
                    (mp) => mp.month !== month
                ),
            };
        });

        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            projections: {
                ...projections,
                organizationProjections: updatedOrgProjections,
            },
        };

        this.setState({ ...this.state, revenueConfig: updatedConfig }).then(() => {
            this.calculateProjectedRevenue();
        });
        this.saveConfig(updatedConfig);
    }

    clearProjections(orgId: string) {
        const projections = this.state.revenueConfig.projections;
        if (!projections) return;

        const updatedOrgProjections = projections.organizationProjections.filter(
            (op) => op.organizationId !== orgId
        );

        const updatedConfig: RevenueConfig = {
            ...this.state.revenueConfig,
            projections: {
                ...projections,
                organizationProjections: updatedOrgProjections,
            },
        };

        this.setState({ ...this.state, revenueConfig: updatedConfig }).then(() => {
            this.calculateProjectedRevenue();
        });
        this.saveConfig(updatedConfig);
    }

    getAllProjectionMonths(): string[] {
        const projections = this.state.revenueConfig.projections;
        if (!projections) return [];

        const monthSet = new Set<string>();
        for (const orgProjection of projections.organizationProjections) {
            for (const mp of orgProjection.monthlyProjections) {
                monthSet.add(mp.month);
            }
        }

        return Array.from(monthSet).sort();
    }

    calculateProjectedRevenue() {
        const projections = this.state.revenueConfig.projections;
        if (!projections) {
            this.setState({ ...this.state, projectedRevenueByOrg: [] });
            return;
        }

        const projectedRevenueByOrg: ProjectedOrganizationRevenue[] = [];

        for (const orgConfig of this.state.revenueConfig.organizations) {
            const orgId = orgConfig.organizationId;
            const orgProjection = projections.organizationProjections.find(
                (op) => op.organizationId === orgId
            );

            const currentLocationCount = this.getCurrentLocationCount(orgId);
            const averageRevenuePerLocation = this.getAverageRevenuePerLocation(orgId);

            const monthlyProjections: ProjectedMonthRevenue[] = [];

            if (orgProjection) {
                for (const mp of orgProjection.monthlyProjections) {
                    monthlyProjections.push({
                        month: mp.month,
                        locationCount: mp.locationCount,
                        averageRevenuePerLocation,
                        projectedRevenue: mp.locationCount * averageRevenuePerLocation,
                    });
                }
            }

            projectedRevenueByOrg.push({
                organizationId: orgId,
                organizationName: orgConfig.organizationName || orgId,
                currentLocationCount,
                averageRevenuePerLocation,
                monthlyProjections,
            });
        }

        this.setState({ ...this.state, projectedRevenueByOrg });
    }
}
